import { state } from '@react-rxjs/core'
import { createSignal } from '@react-rxjs/utils'
import { useEffect, useState } from 'react'
import { BehaviorSubject, distinctUntilChanged, map, Observable } from 'rxjs'

import { Dispensary } from './types'

const [dispensaryChange$, setDispensary] = createSignal<Dispensary | undefined>()

export { setDispensary }

export const latestDispensary$ = state(dispensaryChange$, null)

const [eScriptCartUpdateStatusChange$, setEScriptCartUpdateStatus] = createSignal<
  'idle' | 'in_progress'
>()

export { setEScriptCartUpdateStatus }

export const latestEScriptCartUpdateStatus = state(eScriptCartUpdateStatusChange$, 'idle')

// A counter, so a session that ends after its page unmounted cannot clear one that started since
const pendingSessions = new BehaviorSubject(0)

export const startDispensarySession = (): void => pendingSessions.next(pendingSessions.value + 1)

export const endDispensarySession = (): void =>
  pendingSessions.next(Math.max(0, pendingSessions.value - 1))

/**
 * True while any dispensary session is in flight, from the moment one starts until every one that
 * started has finished or left with its page, whatever their outcomes. The checkout machine reads
 * only this, so a later rewrite of hydration can re-derive it in one place.
 */
export const hydrating$: Observable<boolean> = pendingSessions.pipe(
  map((count) => count > 0),
  distinctUntilChanged(),
)

// read on the first render, so an effect committed with it never sees a session as not started
export const useDispensaryHydrating = (): boolean => {
  const [hydrating, setHydrating] = useState(() => pendingSessions.value > 0)
  useEffect(() => {
    const subscription = hydrating$.subscribe(setHydrating)
    return () => subscription.unsubscribe()
  }, [])
  return hydrating
}
